/**
 * ****************************************************************************
 * @description     :   Config route for all app
 * @created at      :   2024/05/22
 * @created by      :   ANS903 – phuongdt@ans-asia.com
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

import repository from '../repository';

/**
 * Import libraries
 */
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';
import messages, { MSG_TITLE, MSG_TYPE } from '../utils/messages';
import _ from 'lodash';
// import Router from '@/router';

/**
 * Import other part of router
 */
import AuthRoutes from '@/router/auth-router';
import MNGRoutes from '@/router/mng-router';
import CUSRoutes from '@/router/cus-router';
import APPRoutes from '@/router/app-router';
import PTJRoutes from '@/router/ptj-router';
import helpers from '@/utils/helpers';

/**
 * Import component of view
 */
const PageNotFound = () => import('@/views/common/404');
const ServerError = () => import('@/views/common/500');

/**
 * Defined screens has report
 */
/**
 * Defined some base router
 */
const baseRoutes = [
    {
        path: '/404',
        name: 'PageNotFound',
        component: PageNotFound,
        meta: {
            title: 'エラー 404'
        }
    },
    {
        path: '/500',
        name: 'ServerError',
        component: ServerError,
        meta: {
            title: 'エラー 500'
        }
    },
    {
        path: '/logout',
        name: 'Logout',
        meta: {
            title: 'Logout',
            layout: 'MainLayout'
        },
        beforeEnter: (to, from, next) => {
            // call api to remove token
            try {
                // stop listening reverb server
                const { personal_id = '' } = store?.state?.app?.user ?? {};
                const authority = localStorage.getItem('authority');

                authority === '1' && store.dispatch('login/stopListening', personal_id);

                repository.logout().then((res) => {
                    const { data } = res;
                    if (data.Code === 200) {
                        localStorage.removeItem('token');
                        sessionStorage.removeItem('historyURL');
                        store.commit('app/resetUserInfo');
                        store.commit('app/setLogout', false);
                        store.commit('app/setLogin', false);
                        if (authority == 1) {
                            next('/CUS_System/login');
                        } else {
                            next('/MNG_System/login');
                        }
                        // localStorage.clear();
                        helpers.clearLocalStorageExcept(['listMenu']);
                    }
                });
            } catch (e) {
                console.log('Action getAppState: ' + e.message);
            }
        }
    },
    {
        path: '/',
        name: 'Host',
        beforeEnter: (to, from, next) => {
            try {
                const authority = localStorage.getItem('authority');
                if (!authority) {
                    next('/CUS_System/login');
                } else {
                    next({ name: 'PageNotFound' });
                }
            } catch (e) {
                console.log('Action getAppState: ' + e.message);
            }
        }
    },
    {
        path: '/cache/clear-all',
        name: 'ClearCache',
        component: {
            template: '<div>{{ message }}</div>',
            data() {
                return {
                    message: 'Clearing cache...',
                    countdown: 3.0,
                    countdownInterval: null
                };
            },
            async mounted() {
                try {
                    // Call Laravel API to clear cache using repository
                    const response = await repository.clearAllCache();
                    const { data } = response;

                    if (data.Code === 200) {
                        // Update template message with countdown
                        this.message = `✅ Cache cleared successfully! Going back in ${this.countdown}s...`;

                        // Start countdown timer
                        this.countdownInterval = setInterval(() => {
                            this.countdown -= 1;
                            if (this.countdown <= 0) {
                                clearInterval(this.countdownInterval);
                                this.$router.go(-1);
                            } else {
                                this.message = `✅ Cache cleared successfully! Going back in ${this.countdown}s...`;
                            }
                        }, 1000);
                    } else {
                        this.message = '❌ Failed to clear cache!';
                    }
                } catch (error) {
                    console.error('Failed to clear cache:', error);
                    this.message = '❌ Error occurred while clearing cache!';
                }
            },
            beforeUnmount() {
                // Clean up interval when component is destroyed
                if (this.countdownInterval) {
                    clearInterval(this.countdownInterval);
                }
            }
        },
        meta: {
            title: 'Clear Cache'
        }
    },
    {
        path: '/:catchAll(.*)', // Unrecognized path automatically matches 404
        redirect: '/404'
    }
];

/**
 * Concat all parts of router
 */
const routes = baseRoutes.concat(AuthRoutes).concat(MNGRoutes).concat(CUSRoutes).concat(APPRoutes).concat(PTJRoutes);

/**
 * Defined Vue Router
 */
const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior() {
        return { top: 0 };
    }
});

/**
 * Change title of website on browser when change page
 * -----------------------------------------
 * @author : PhuongDT - 2024/05/22 - create
 * @params : to - object - Data of router will redirect to
 * @params : from - object - Data of current router
 * @params : next - function - Continue redirect
 * @access : public
 */
router.beforeEach((to, from, next) => {
    const func_backing = () => {
        let historyURL = JSON.parse(sessionStorage.getItem('historyURL'));
        if (!historyURL) {
            historyURL = [];
        }
        let lastObj = _.last(historyURL);
        if (store._state.data.app.isBacking) {
            store.commit('app/setBacking', false);
        } else {
            if (from.name) {
                let backup = from.name.toLowerCase() + '_backup';

                if (lastObj && lastObj.path != from.path) {
                    historyURL.push({
                        name: from.name,
                        path: from.path,
                        p: from.query,
                        ...(sessionStorage.getItem(backup) ? { backupData: sessionStorage.getItem(backup) } : {})
                    });

                    sessionStorage.removeItem(backup);
                } else {
                    historyURL.push({
                        name: 'Dashboard',
                        path: from.path,
                        p: from.query
                    });
                }
            } else {
                if (historyURL.length === 0 && from.path == '/') {
                    historyURL.push({
                        name: 'Dashboard',
                        path: from.path,
                        p: from.query
                    });
                }
            }
            // }
            sessionStorage.setItem('historyURL', JSON.stringify(historyURL));
        }
        if (to.path == '/') {
            sessionStorage.removeItem('historyURL');
        }
    };
    func_backing();
    const func_logout = () => {
        localStorage.removeItem('token');
        sessionStorage.removeItem('historyURL');
        store.commit('app/resetUserInfo', false);
        store.commit('app/setLogout', false);
        store.commit('app/setLogin', false);
        // localStorage.clear();
        helpers.clearLocalStorageExcept(['listMenu']);
    };
    if (store._state.data.app.isLogout) {
        func_logout();
    }

    const func_beforeEach = async (to, from, next) => {
        if (to.path.toUpperCase().startsWith('/APP')) {
            return next();
        }
        if (to.path.toUpperCase().startsWith('/PTJ')) {
            document.title = 'M2　アプリ';
            return next();
        }
        async function fetchUserIP() {
            const response = await fetch('https://api.ipify.org?format=json');
            const data = await response.json();
            return data.ip;
        }
        if (to.path === '/view_log' || to.path.startsWith('/view_log/detail/')) {
            const userIP = await fetchUserIP();
            const allowedIPs = [
                '113.160.225.193', //IP ANS DN
                '118.70.128.67', //IP ANS HN
                '124.36.35.146', //IP ANS JP
                '127.0.0.1'
            ];
            if (allowedIPs.includes(userIP)) {
                next();
                return;
            } else {
                next({ name: 'PageNotFound' });
                return;
            }
        }

        const noAuthenticate = [
            '/404',
            '/CUS_Register001',
            '/CUS_Register002',
            '/CUS_Register003',
            '/CUS_Register004',
            '/CUS_Register005',
            '/CUS_Start001',
            '/CUS_Start002',
            '/CUS_Start003',
            '/CUS_Start004',
            '/CUS_Start005',
            '/CUS_Start011',
            '/CUS_Start021',
            '/CUS_Start022',
            '/CUS_Start023',
            '/CUS_Start024',
            '/CUS_Start025',
            '/CUS_Start026',
            '/view_log',
            '/view_log/detail/',
            '/CUS_FAQ',
            '/CUS_FAQ/categories',
            '/CUS_FAQ/sections',
            '/CUS_FAQ/searchKeyword',
            '/CUS_FAQ/articles',
            '/CUS_FAQ/contact',
            '/cache/clear-all',
            'CUS_System903',
            'MNG_System903'
        ];
        const index = noAuthenticate.findIndex((routeName) => {
            const mainPath = to.path.split('/');
            return routeName.toUpperCase().includes(mainPath[1].toUpperCase()) || to.path.startsWith('/CUS_FAQ');
        });

        if (index > -1) {
            // fix bug 2024/10/15
            if (to.name == 'CUS_Start005') {
                const user = localStorage.getItem('userInfo');
                store.commit('app/setUserInfo', user ? JSON.parse(user) : {});
            }

            next();
            if (to.path.includes('CUS_FAQ')) {
                document.title = 'M2　ヘルプセンター';
            } else {
                document.title = 'ムーヴ　M2';
            }
            return;
        }

        store.commit('app/hideHeaderError');
        const publicPages = [
            '/CUS_System901',
            '/CUS_System902',
            '/CUS_System903',
            'MNG_System903',
            '/CUS_System/login',
            '/MNG_System/login',
            '/MNG_System901',
            '/MNG_System902',
            '/CUS_System004'
        ];

        // lấy các thông tin user
        // const authRequired = !publicPages.includes(to.path);
        const authority = localStorage.getItem('authority');
        const user = localStorage.getItem('userInfo');
        store.commit('app/setUserInfo', user ? JSON.parse(user) : {});
        let loggedIn = false;
        const token = localStorage.getItem('token');
        const tokenTimeout = localStorage.getItem('tokenTimeout');

        // check đang có tài khoản login ko
        if (token && tokenTimeout && new Date(tokenTimeout) >= new Date()) {
            loggedIn = true;
            store.commit('app/setLogin', true);
        } else {
            localStorage.removeItem('token');
            localStorage.removeItem('tokenTimeout');
            localStorage.removeItem('userInfo');
        }

        // set title
        let title = '';
        const routeName = to.name;

        if (routeName.toLowerCase().includes('mng') || authority === '2') {
            title = 'M2　管理画面';
        } else if (routeName.toLowerCase().includes('cus') || authority === '1') {
            title = 'ムーヴ　M2';
        }

        document.title = title;

        // ko có tk login
        if (!loggedIn && !['/MNG_System/login', '/CUS_System/login'].includes(to.path)) {
            localStorage.setItem('beforeUrl', window.location.pathname);
            if (!store._state.data.app.isLogin) {
                store.commit('app/setLogin', false);
                const pathTo = to.path.toUpperCase();
                if (!publicPages.map((page) => page.toUpperCase()).includes(pathTo)) {
                    router.push(pathTo.includes('MNG') ? '/MNG_System/login' : '/CUS_System/login');
                }
            } else {
                store.commit('app/showModalMessage', {
                    type: MSG_TYPE.ERROR,
                    title: MSG_TITLE.E999,
                    content: messages.E401,
                    callback: () => {
                        store.commit('app/setLogin', false);
                        if (routeName.includes('MNG') || authority == 2) {
                            router.push('/MNG_System/login');
                        } else if (routeName.includes('CUS') || authority == 1) {
                            router.push('/CUS_System/login');
                        }
                    }
                });
                return;
            }
        } else {
            if (publicPages.map((page) => page.toUpperCase()).includes(to.path.toUpperCase())) {
                if (to.path === '/MNG_System/login' || to.path === '/CUS_System/login') {
                    func_logout();
                } else if (to.path !== '/CUS_System901' && to.path !== '/MNG_System901') {
                    router.push(authority == 2 ? '/MNG_System001' : '/CUS_System001');
                }
            }
        }

        next();
    };
    func_beforeEach(to, from, next);
});

export default router;
